import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAnimateList, AnimationOverview } from '../../../backend/useAnimateList';
import { useUser } from '../../../backend/useUser';
import Gallery from '../../website/Gallery';
import { Project } from '../../website/ProjectGrid';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../ui/components/LoadingSpinner';

const GalleryContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: visible;
`;

interface AnimateGalleryProps {
    colormode: 'light' | 'dark';
    tabs: Array<{ name: string; icon?: string; disabled?: boolean }>
}

const ITEMS_PER_PAGE = 8;

const AnimateGallery: React.FC<AnimateGalleryProps> = ({ colormode, tabs }) => {
    const [myAnims, setMyAnims] = useState<Project[]>([]);
    const [communityAnims, setCommunityAnims] = useState<Project[]>([]);
    
    const [myAnimPage, setMyAnimPage] = useState(1);
    const [communityPage, setCommunityPage] = useState(1);
    
    const [myAnimLoading, setMyAnimLoading] = useState(true);
    const [communityLoading, setCommunityLoading] = useState(true);
    const [myAnimHasMore, setMyAnimHasMore] = useState(true);
    const [communityHasMore, setCommunityHasMore] = useState(true);
    
    const [activeTab, setActiveTab] = useState('My Animations');
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    
    const { user, loading: userLoading } = useUser();
    const { listAllAnimations, listMyAnimations } = useAnimateList();
    const navigate = useNavigate();

    useEffect(() => {
        if (!userLoading && !user) {
            setActiveTab('Community');
        }
    }, [user, userLoading]);

    const animToProject = useCallback((anim: AnimationOverview): Project => {
    const mediaUrl = anim.animationVideo || anim.referenceImage || '/assets/animation-placeholder.png';

    return {
        id: anim.id,
        name: anim.animation_name || '',
        username: anim.user_tag || '',
        imageUrl: mediaUrl,
        linkType: 'animate',
    };
}, []);

    const loadMyAnims = useCallback(async (pageNum: number) => {
        if (userLoading || !user) return;
        
        setMyAnimLoading(true);
        try {
            const { animations, hasMore } = await listMyAnimations(user.id, pageNum, ITEMS_PER_PAGE);
            const newProjects = animations.map(animToProject);
            
            setMyAnims(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
            setMyAnimHasMore(hasMore);
            
            if (pageNum === 1 && newProjects.length === 0 && !initialLoadDone && activeTab === 'My Animations') {
                setActiveTab('Community');
            }
        } catch (error) {
            console.error('Error loading my animations:', error);
            setMyAnimHasMore(false);
        } finally {
            setMyAnimLoading(false);
            setInitialLoadDone(true);
        }
    }, [user, userLoading, listMyAnimations, animToProject, initialLoadDone, activeTab]);

    const loadCommunityAnims = useCallback(async (pageNum: number) => {
        if (userLoading) return;
        
        setCommunityLoading(true);
        try {
            const { animations, hasMore } = await listAllAnimations(pageNum, ITEMS_PER_PAGE);
            const newProjects = animations.map(animToProject);
            
            setCommunityAnims(prev => pageNum === 1 ? newProjects : [...prev, ...newProjects]);
            setCommunityHasMore(hasMore);
        } catch (error) {
            console.error('Error loading community animations:', error);
            setCommunityHasMore(false);
        } finally {
            setCommunityLoading(false);
            setInitialLoadDone(true);
        }
    }, [userLoading, listAllAnimations, animToProject]);

    useEffect(() => {
        if (userLoading) return;
        
        if (activeTab === 'My Animations') {
            if (user && myAnims.length === 0) {
                loadMyAnims(1);
            }
        } else {
            if (communityAnims.length === 0) {
                loadCommunityAnims(1);
            }
        }
    }, [activeTab, user, userLoading, loadMyAnims, loadCommunityAnims, myAnims.length, communityAnims.length]);

    const handleLoadMore = useCallback(async () => {
        if (activeTab === 'My Animations') {
            const next = myAnimPage + 1;
            setMyAnimPage(next);
            await loadMyAnims(next);
        } else {
            const next = communityPage + 1;
            setCommunityPage(next);
            await loadCommunityAnims(next);
        }
    }, [activeTab, myAnimPage, communityPage, loadMyAnims, loadCommunityAnims]);

    const handleProjectClick = useCallback((project: Project) => {
        navigate(`/animate/${project.id}`);
    }, [navigate]);

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab);
    }, []);

    const visibleTabs = useMemo(() => {
        if (!user) return []; 
        return tabs;
    }, [user, tabs]);

    const currentProjects = activeTab === 'My Animations' ? myAnims : communityAnims;
    const currentLoading = activeTab === 'My Animations' ? myAnimLoading : communityLoading;
    const currentHasMore = activeTab === 'My Animations' ? myAnimHasMore : communityHasMore;

    if (userLoading) {
        return <LoadingSpinner colorMode={colormode} />;
    }

    return (
        <GalleryContainer>
            <Gallery
                type="Animation"
                colormode={colormode}
                projects={currentProjects}
                loading={currentLoading}
                hasMore={currentHasMore}
                onLoadMore={handleLoadMore}
                onProjectClick={handleProjectClick}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                tabs={visibleTabs}
                userLoading={userLoading}
            />
        </GalleryContainer>
    );
};

export default AnimateGallery;